import React, { createContext, useContext, useMemo, useState } from 'react';

/**
 * Where a page's title and actions go: the fixed top bar.
 *
 * They used to live inside each page's own scrolling content, so Publish and
 * Save draft left the screen the moment anyone started editing — on exactly
 * the screens that are long and where you edit for a while.
 *
 * The actions travel as a DOM node to portal into, not as an element held in
 * state. That distinction matters: `actions` is rebuilt on every render, so
 * putting it in state would set state on every render, re-render the
 * provider, rebuild the element and set state again — an endless loop. A
 * portal sidesteps it entirely and keeps the buttons live, so "Publishing…"
 * still appears while it publishes.
 *
 * The title and subtitle are strings, which compare equal, so they can be
 * state.
 *
 * There are two slots, not one. A screen that is *inside* something — the
 * page editor, which is one page of many — needs its way back and its name at
 * the head of the bar, not at the end of it with the buttons. Same mechanism,
 * opposite end.
 */
const PageChromeContext = createContext({
  title: null,
  setTitle: null,
  subtitle: null,
  setSubtitle: null,
  actionSlot: null,
  setActionSlot: () => {},
  leadingSlot: null,
  setLeadingSlot: () => {},
  hasActions: false,
  setHasActions: () => {},
});

export function PageChromeProvider({ children }) {
  const [title, setTitle] = useState(null);
  const [subtitle, setSubtitle] = useState(null);
  const [actionSlot, setActionSlot] = useState(null);
  const [leadingSlot, setLeadingSlot] = useState(null);
  // A boolean, not the actions themselves — see above. It only tells the bar
  // whether to occupy any space.
  const [hasActions, setHasActions] = useState(false);

  const value = useMemo(
    () => ({
      title, setTitle, subtitle, setSubtitle,
      actionSlot, setActionSlot, leadingSlot, setLeadingSlot,
      hasActions, setHasActions,
    }),
    [title, subtitle, actionSlot, leadingSlot, hasActions],
  );

  return <PageChromeContext.Provider value={value}>{children}</PageChromeContext.Provider>;
}

export const usePageChromeSlot = () => useContext(PageChromeContext);

export default PageChromeContext;
